/**
 * Stage 10–12 professional corpus — longer pedagogical studies for
 * repertoire, virtuoso technique, and performance prep.
 * These are complete practice works (not fake abridged masterpieces).
 */

import type { HandId, MusicPiece, NoteEvent } from "./types.js";

function seq(
  midis: number[],
  step = 0.5,
  start = 0,
  hand: HandId = "right",
  velocity = 92,
): NoteEvent[] {
  return midis.map((midi, i) => ({
    beat: start + i * step,
    duration: step * 0.9,
    midi,
    velocity,
    hand,
  }));
}

function chordBeats(midis: number[], beats: number[], dur = 2, vel = 90): NoteEvent[] {
  return beats.flatMap((beat) =>
    midis.map((midi) => ({
      beat,
      duration: dur,
      midi,
      velocity: vel,
      hand: "both" as const,
    })),
  );
}

function piece(p: MusicPiece): MusicPiece {
  return p;
}

const MAJOR = [0, 2, 4, 5, 7, 9, 11, 12];

function scaleRun(root: number, step = 0.25): NoteEvent[] {
  const up = MAJOR.map((n) => root + n);
  return seq([...up, ...up.slice(0, -1).reverse()], step);
}

/** Two-voice invention-style study (original pedagogical miniature). */
const INV_STUDY = piece({
  id: "pro-baroque-invention-study",
  title: "Two-Voice Invention Study",
  category: "repertoire",
  bpm: 88,
  timeSignature: "4/4",
  key: "C major",
  hand: "both",
  stage: 10,
  description: "Independent RH subject against LH countersubject — complete study.",
  notes: [
    ...seq([60, 62, 64, 65, 67, 65, 64, 62, 60, 64, 67, 72], 0.5, 0, "right", 96),
    ...seq([48, 52, 55, 48, 47, 48, 52, 55, 48, 52, 55, 60], 0.5, 0, "left", 78),
    ...seq([72, 71, 69, 67, 65, 64, 62, 60], 0.5, 6, "right", 94),
    ...seq([60, 55, 52, 48, 47, 48, 43, 48], 0.5, 6, "left", 76),
  ],
});

const SONATINA = piece({
  id: "pro-classical-sonatina-a",
  title: "Classical Sonatina Exposition Study",
  category: "repertoire",
  bpm: 100,
  timeSignature: "4/4",
  key: "C major",
  hand: "both",
  stage: 10,
  description: "Theme + Alberti LH — complete pedagogical sonatina exposition.",
  notes: [
    ...seq([72, 71, 69, 67, 65, 64, 62, 60, 64, 67, 72, 76], 0.5, 0, "right", 98),
    ...[0, 1, 2, 3, 4, 5, 6, 7].flatMap((b) =>
      [
        { beat: b, duration: 0.25, midi: 48, velocity: 72, hand: "left" as const },
        { beat: b + 0.25, duration: 0.25, midi: 55, velocity: 68, hand: "left" as const },
        { beat: b + 0.5, duration: 0.25, midi: 52, velocity: 70, hand: "left" as const },
        { beat: b + 0.75, duration: 0.25, midi: 55, velocity: 68, hand: "left" as const },
      ],
    ),
  ],
});

const NOCTURNE = piece({
  id: "pro-romantic-nocturne-study",
  title: "Romantic Lyric Study",
  category: "repertoire",
  bpm: 72,
  timeSignature: "4/4",
  key: "A minor",
  hand: "both",
  stage: 10,
  description: "Cantabile RH over rolled LH harmony — complete lyric study.",
  pedalCues: [
    { beat: 0, down: true },
    { beat: 4, down: false },
    { beat: 4, down: true },
    { beat: 8, down: false },
  ],
  notes: [
    ...seq([69, 71, 72, 74, 76, 74, 72, 71, 69, 67, 65, 64, 62, 60, 64, 69], 0.5, 0, "right", 88),
    ...chordBeats([45, 52, 57], [0, 2, 4, 6], 2, 70),
  ],
});

const PRELUDE = piece({
  id: "pro-prelude-pattern-study",
  title: "Prelude Pattern Study",
  category: "etude",
  bpm: 96,
  timeSignature: "4/4",
  key: "G major",
  hand: "right",
  stage: 10,
  description: "Continuous broken-chord figuration for endurance.",
  notes: seq(
    [55, 59, 62, 67, 62, 59, 55, 59, 62, 67, 71, 67, 62, 59, 55, 50].flatMap((n) => [
      n,
      n + 4,
      n + 7,
      n + 4,
    ]),
    0.25,
  ),
});

const FUGUE_SUBJ = piece({
  id: "pro-fugue-subject-drill",
  title: "Fugue Subject Memory Drill",
  category: "repertoire",
  bpm: 84,
  timeSignature: "4/4",
  key: "D minor",
  hand: "right",
  stage: 10,
  description: "Memorize and transpose a short subject.",
  notes: seq([62, 65, 69, 67, 65, 62, 60, 62, 65, 69, 74, 72, 69, 65, 62], 0.5),
});

const OCTAVE_ETUDE = piece({
  id: "pro-octave-etude",
  title: "Octave Power Etude",
  category: "etude",
  bpm: 88,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 11,
  description: "Controlled octave outlines — technique, not brute force.",
  notes: [60, 62, 64, 65, 67, 69, 71, 72, 71, 69, 67, 65, 64, 62, 60].flatMap((m, i) => [
    { beat: i, duration: 0.9, midi: m, velocity: 96, hand: "right" as const },
    { beat: i, duration: 0.9, midi: m + 12, velocity: 90, hand: "right" as const },
  ]),
});

const DOUBLE_THIRD = piece({
  id: "pro-double-thirds-c",
  title: "Double Thirds Circuit",
  category: "etude",
  bpm: 76,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 11,
  description: "Diatonic thirds ascending/descending.",
  notes: [60, 62, 64, 65, 67, 69, 71, 72, 71, 69, 67, 65, 64, 62, 60].flatMap((m, i) => [
    { beat: i * 0.5, duration: 0.45, midi: m, velocity: 92, hand: "right" as const },
    { beat: i * 0.5, duration: 0.45, midi: m + 4, velocity: 88, hand: "right" as const },
  ]),
});

const ALL_KEY_BURST = piece({
  id: "pro-all-key-scale-burst",
  title: "All-Key Scale Burst",
  category: "scale",
  bpm: 100,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 11,
  description: "One-octave majors through sharp keys in sequence.",
  notes: [60, 62, 64, 65, 67, 69, 71, 72].flatMap((root, i) =>
    scaleRun(root, 0.2).map((n) => ({ ...n, beat: n.beat + i * 4 })),
  ),
});

const ARP_STORM = piece({
  id: "pro-arp-storm",
  title: "Arpeggio Storm",
  category: "arpeggio",
  bpm: 104,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 11,
  description: "Wide arpeggio outlines for agility.",
  notes: seq(
    [48, 52, 55, 60, 64, 67, 72, 76, 72, 67, 64, 60, 55, 52, 48].concat(
      [50, 53, 57, 62, 65, 69, 74, 77, 74, 69, 65, 62, 57, 53, 50],
    ),
    0.25,
  ),
});

const TRILL_PREP = piece({
  id: "pro-trill-ornament-prep",
  title: "Trill & Mordent Prep",
  category: "etude",
  bpm: 72,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 11,
  description: "Neighbor oscillations into resolution.",
  notes: [60, 62, 64, 65, 67].flatMap((m, i) => {
    const start = i * 2;
    return [
      { beat: start, duration: 0.125, midi: m, velocity: 88, hand: "right" as const },
      { beat: start + 0.125, duration: 0.125, midi: m + 1, velocity: 84, hand: "right" as const },
      { beat: start + 0.25, duration: 0.125, midi: m, velocity: 88, hand: "right" as const },
      { beat: start + 0.375, duration: 0.125, midi: m + 1, velocity: 84, hand: "right" as const },
      { beat: start + 0.5, duration: 0.125, midi: m, velocity: 90, hand: "right" as const },
      { beat: start + 0.625, duration: 0.125, midi: m + 1, velocity: 86, hand: "right" as const },
      { beat: start + 0.75, duration: 1.25, midi: m, velocity: 94, hand: "right" as const },
    ];
  }),
});

const VOICING = piece({
  id: "pro-voicing-balance",
  title: "Melody vs Accompaniment Balance",
  category: "etude",
  bpm: 80,
  timeSignature: "4/4",
  key: "F major",
  hand: "both",
  stage: 11,
  description: "RH melody strong, LH soft — dynamic independence.",
  notes: [
    ...seq([65, 67, 69, 70, 72, 70, 69, 67, 65], 0.5, 0, "right", 100),
    ...chordBeats([41, 48, 53], [0, 2, 4, 6], 2, 60),
  ],
});

const MOCK_JURY_A = piece({
  id: "pro-mock-jury-a",
  title: "Mock Jury Work A",
  category: "repertoire",
  bpm: 92,
  timeSignature: "4/4",
  key: "G major",
  hand: "both",
  stage: 12,
  description: "Uninterrupted jury-length take A.",
  notes: [
    ...seq([67, 69, 71, 72, 74, 72, 71, 69, 67, 66, 67, 71, 74, 79], 0.5, 0, "right", 96),
    ...chordBeats([43, 50, 55], [0, 2, 4, 6], 2, 72),
    ...seq([79, 76, 74, 71, 67, 66, 67, 62, 59, 55], 0.5, 7, "right", 94),
  ],
});

const MOCK_JURY_B = piece({
  id: "pro-mock-jury-b",
  title: "Mock Jury Work B",
  category: "repertoire",
  bpm: 76,
  timeSignature: "3/4",
  key: "D minor",
  hand: "both",
  stage: 12,
  description: "Contrasting lyrical jury work B.",
  notes: [
    ...seq([69, 70, 72, 74, 72, 70, 69, 67, 65, 64, 62, 60, 62, 65, 69], 0.5, 0, "right", 86),
    ...chordBeats([41, 48, 53], [0, 3, 6, 9], 2.5, 68),
  ],
});

const SIGHT_PRO = piece({
  id: "pro-sight-open-score",
  title: "Advanced Sight Line",
  category: "sight",
  bpm: 88,
  timeSignature: "4/4",
  key: "E-flat major",
  hand: "right",
  stage: 12,
  description: "Unseen chromatic-leaning line for pro sight practice.",
  notes: seq(
    [63, 65, 67, 68, 70, 72, 70, 68, 67, 65, 63, 62, 63, 67, 70, 75, 74, 72, 70, 68, 67, 65, 63],
    0.5,
  ),
});

const TRANSPOSE_DRILL = piece({
  id: "pro-transpose-phrase",
  title: "Transposition Phrase Lab",
  category: "theory",
  bpm: 84,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 12,
  description: "Same motif moved by step — train mental transposition.",
  notes: [0, 2, 4, 5].flatMap((shift, i) =>
    seq([60, 62, 64, 65, 67, 64, 60].map((m) => m + shift), 0.5, i * 4),
  ),
});

const CADENZA = piece({
  id: "pro-cadenza-freedom",
  title: "Cadenza Freedom Study",
  category: "etude",
  bpm: 70,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 12,
  description: "Rubato-friendly flourish into a firm cadence.",
  notes: [
    ...seq([72, 74, 76, 77, 79, 81, 83, 84, 83, 81, 79, 77, 76, 74, 72], 0.25, 0, "right", 90),
    ...chordBeats([48, 52, 55, 60], [4], 3, 100),
  ],
});

const FINAL_PRO_RECITAL = piece({
  id: "pro-final-diploma-recital",
  title: "Professional Diploma Recital Capstone",
  category: "repertoire",
  bpm: 90,
  timeSignature: "4/4",
  key: "C major",
  hand: "both",
  stage: 12,
  description: "Full uninterrupted professional-track capstone.",
  pedalCues: [
    { beat: 0, down: true },
    { beat: 8, down: false },
    { beat: 8, down: true },
    { beat: 16, down: false },
  ],
  notes: [
    ...seq([60, 64, 67, 72, 76, 72, 67, 64, 60, 62, 64, 65, 67, 69, 71, 72], 0.5, 0, "right", 96),
    ...chordBeats([36, 43, 48], [0, 4, 8, 12], 3.5, 74),
    ...seq([72, 71, 69, 67, 65, 64, 62, 60, 59, 60], 0.5, 8, "right", 94),
  ],
});

export const PRO_PIECES: MusicPiece[] = [
  INV_STUDY,
  SONATINA,
  NOCTURNE,
  PRELUDE,
  FUGUE_SUBJ,
  OCTAVE_ETUDE,
  DOUBLE_THIRD,
  ALL_KEY_BURST,
  ARP_STORM,
  TRILL_PREP,
  VOICING,
  MOCK_JURY_A,
  MOCK_JURY_B,
  SIGHT_PRO,
  TRANSPOSE_DRILL,
  CADENZA,
  FINAL_PRO_RECITAL,
];
